/**
 * Dispatch Dashboard - Enterprise Interface
 * Enterprise-level dispatch management with drag-and-drop job assignment,
 * real-time GPS tracking, route optimization, and technician performance monitoring
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import {
  MapPin,
  Clock,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Truck,
  Phone,
  Calendar,
  DollarSign,
  Activity,
  Filter,
  Search,
  Plus,
  RefreshCw
} from 'lucide-react';

interface Technician {
  id: string;
  name: string;
  status: 'available' | 'busy' | 'offline' | 'break';
  currentLocation?: {
    latitude: number;
    longitude: number;
    address: string;
  };
  currentJob?: string;
  skillSet: string[];
  performance: {
    jobsCompleted: number;
    rating: number;
    onTimeRate: number;
  };
  todaysJobs: number;
  efficiency: number;
}

interface Job {
  id: string;
  title: string;
  customer: string;
  address: string;
  priority: 'low' | 'medium' | 'high' | 'emergency';
  status: 'unassigned' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  scheduledTime: string;
  estimatedDuration: number;
  skillRequired: string[];
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  technicianId?: string;
  urgency: number;
}

interface DispatchMetrics {
  totalJobs: number;
  unassignedJobs: number;
  activeTechnicians: number;
  completedToday: number;
  avgResponseTime: number;
  customerSatisfaction: number;
  revenueToday: number;
  efficiency: number;
}

export default function DispatchDashboard() {
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [unassignedJobs, setUnassignedJobs] = useState<Job[]>([]);
  const [metrics, setMetrics] = useState<DispatchMetrics>({
    totalJobs: 0,
    unassignedJobs: 0,
    activeTechnicians: 0,
    completedToday: 0,
    avgResponseTime: 0,
    customerSatisfaction: 0,
    revenueToday: 0,
    efficiency: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedTechnician, setSelectedTechnician] = useState<string | null>(null);
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadDashboardData();
    // Set up real-time subscriptions
    setupRealtimeSubscriptions();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadTechnicians(),
        loadJobs(),
        loadMetrics()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTechnicians = async () => {
    // Mock data for demonstration - replace with actual API calls
    const mockTechnicians: Technician[] = [
      {
        id: '1',
        name: 'Mike Johnson',
        status: 'available',
        currentLocation: { latitude: 40.7128, longitude: -74.0060, address: '123 Main St, NYC' },
        skillSet: ['HVAC', 'Plumbing', 'Electrical'],
        performance: { jobsCompleted: 8, rating: 4.8, onTimeRate: 95 },
        todaysJobs: 6,
        efficiency: 92
      },
      {
        id: '2',
        name: 'Sarah Wilson',
        status: 'busy',
        currentLocation: { latitude: 40.7589, longitude: -73.9851, address: '456 Oak Ave, NYC' },
        currentJob: 'Emergency Repair - Unit 5B',
        skillSet: ['Plumbing', 'Emergency Response'],
        performance: { jobsCompleted: 12, rating: 4.9, onTimeRate: 98 },
        todaysJobs: 8,
        efficiency: 96
      },
      {
        id: '3',
        name: 'David Chen',
        status: 'break',
        currentLocation: { latitude: 40.7505, longitude: -73.9934, address: '789 Pine St, NYC' },
        skillSet: ['Electrical', 'Smart Home'],
        performance: { jobsCompleted: 6, rating: 4.7, onTimeRate: 88 },
        todaysJobs: 4,
        efficiency: 85
      }
    ];
    setTechnicians(mockTechnicians);
  };

  const loadJobs = async () => {
    // Mock data for demonstration
    const mockJobs: Job[] = [
      {
        id: '1',
        title: 'HVAC System Installation',
        customer: 'John Smith',
        address: '123 Main St, Apartment 5B',
        priority: 'high',
        status: 'unassigned',
        scheduledTime: '2024-01-15T09:00:00Z',
        estimatedDuration: 180,
        skillRequired: ['HVAC'],
        coordinates: { latitude: 40.7128, longitude: -74.0060 },
        urgency: 8
      },
      {
        id: '2',
        title: 'Emergency Plumbing Repair',
        customer: 'Jane Doe',
        address: '456 Oak Ave, Unit 12',
        priority: 'emergency',
        status: 'assigned',
        scheduledTime: '2024-01-15T14:30:00Z',
        estimatedDuration: 90,
        skillRequired: ['Plumbing', 'Emergency Response'],
        coordinates: { latitude: 40.7589, longitude: -73.9851 },
        technicianId: '2',
        urgency: 10
      },
      {
        id: '3',
        title: 'Electrical Panel Upgrade',
        customer: 'Bob Johnson',
        address: '789 Pine St, House',
        priority: 'medium',
        status: 'in_progress',
        scheduledTime: '2024-01-15T10:00:00Z',
        estimatedDuration: 240,
        skillRequired: ['Electrical'],
        coordinates: { latitude: 40.7505, longitude: -73.9934 },
        technicianId: '3',
        urgency: 6
      }
    ];

    setJobs(mockJobs);
    setUnassignedJobs(mockJobs.filter(job => job.status === 'unassigned'));
  };

  const loadMetrics = async () => {
    // Mock metrics data
    setMetrics({
      totalJobs: 24,
      unassignedJobs: 8,
      activeTechnicians: 12,
      completedToday: 16,
      avgResponseTime: 45,
      customerSatisfaction: 4.7,
      revenueToday: 8750,
      efficiency: 89
    });
  };

  const setupRealtimeSubscriptions = () => {
    // Set up real-time subscriptions for live updates
    // This would connect to Supabase real-time subscriptions
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'busy': return 'bg-blue-100 text-blue-800';
      case 'offline': return 'bg-gray-100 text-gray-600';
      case 'break': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'emergency': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const filteredJobs = unassignedJobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = filterPriority === 'all' || job.priority === filterPriority;
    return matchesSearch && matchesPriority;
  });

  return (
    <AdminPageLayout
      title="Dispatch Center"
      subtitle="Enterprise Dispatch Management • Real-time GPS Tracking • Route Optimization"
      actions={
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Job
          </button>
        </div>
      }
    >
      {/* Key Metrics */}
      <StatsGrid cols={4}>
        <StatCard
          label="Unassigned Jobs"
          value={metrics.unassignedJobs}
          icon={AlertTriangle}
          subtitle={`${metrics.totalJobs} total jobs`}
        />
        <StatCard
          label="Active Technicians"
          value={metrics.activeTechnicians}
          icon={Users}
          subtitle={`${technicians.filter(t => t.status === 'available').length} available`}
        />
        <StatCard
          label="Completed Today"
          value={metrics.completedToday}
          icon={CheckCircle}
          subtitle={`${Math.round((metrics.completedToday / metrics.totalJobs) * 100)}% completion rate`}
        />
        <StatCard
          label="Revenue Today"
          value={`$${metrics.revenueToday.toLocaleString()}`}
          icon={DollarSign}
          subtitle={`Avg: $${Math.round(metrics.revenueToday / metrics.completedToday)} per job`}
        />
      </StatsGrid>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Job Board */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Job Board</h2>
              <div className="flex gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search jobs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-64"
                  />
                </div>
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Priorities</option>
                  <option value="emergency">Emergency</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading jobs...</div>
            ) : filteredJobs.length === 0 ? (
              <div className="py-8 text-center text-gray-400">
                No unassigned jobs match your criteria.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredJobs.map((job) => (
                  <div
                    key={job.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('jobId', job.id);
                    }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{job.title}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(job.priority)}`}>
                            {job.priority}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getJobStatusColor(job.status)}`}>
                            {job.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{job.customer} • {job.address}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(job.scheduledTime).toLocaleTimeString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Activity className="w-3 h-3" />
                            {job.estimatedDuration}min
                          </span>
                          <span className={`flex items-center gap-1 ${job.urgency >= 8 ? 'text-red-600' : job.urgency >= 6 ? 'text-orange-600' : 'text-green-600'}`}>
                            <AlertTriangle className="w-3 h-3" />
                            Urgency: {job.urgency}/10
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                          Quick Assign
                        </button>
                        <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                          View Details
                        </button>
                      </div>
                    </div>

                    {job.skillRequired.length > 0 && (
                      <div className="flex gap-1">
                        {job.skillRequired.map((skill, index) => (
                          <span key={index} className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Technician Status Board */}
        <div>
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Technician Status</h2>

            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading technicians...</div>
            ) : (
              <div className="space-y-4">
                {technicians.map((technician) => (
                  <div
                    key={technician.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      selectedTechnician === technician.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedTechnician(
                      selectedTechnician === technician.id ? null : technician.id
                    )}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          technician.status === 'available' ? 'bg-green-500' :
                          technician.status === 'busy' ? 'bg-blue-500' :
                          technician.status === 'break' ? 'bg-yellow-500' :
                          'bg-gray-400'
                        }`} />
                        <div>
                          <h3 className="font-medium text-gray-900">{technician.name}</h3>
                          <p className="text-xs text-gray-600 capitalize">{technician.status}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(technician.status)}`}>
                        {technician.status}
                      </span>
                    </div>

                    {technician.currentLocation && (
                      <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                        <MapPin className="w-3 h-3" />
                        <span>{technician.currentLocation.address}</span>
                      </div>
                    )}

                    {technician.currentJob && (
                      <p className="text-xs text-blue-600 mb-2">{technician.currentJob}</p>
                    )}

                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <div>Today's Jobs: {technician.todaysJobs}</div>
                      <div>Efficiency: {technician.efficiency}%</div>
                      <div>Rating: {technician.performance.rating}⭐</div>
                      <div>On-Time: {technician.performance.onTimeRate}%</div>
                    </div>

                    {selectedTechnician === technician.id && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex gap-1 mb-2">
                          {technician.skillSet.map((skill, index) => (
                            <span key={index} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                              {skill}
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button className="flex-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                            View Schedule
                          </button>
                          <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                            Call
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Real-time Activity Feed */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Live Activity Feed</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">Job Completed</p>
              <p className="text-xs text-green-600">Sarah Wilson completed "Emergency Plumbing Repair" for Jane Doe</p>
            </div>
            <span className="text-xs text-green-600">2 min ago</span>
          </div>

          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Truck className="w-5 h-5 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">Technician En Route</p>
              <p className="text-xs text-blue-600">Mike Johnson is heading to "HVAC Installation" at 123 Main St</p>
            </div>
            <span className="text-xs text-blue-600">5 min ago</span>
          </div>

          <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800">New Emergency Job</p>
              <p className="text-xs text-orange-600">Emergency plumbing repair requested at 456 Oak Ave</p>
            </div>
            <span className="text-xs text-orange-600">8 min ago</span>
          </div>
        </div>
      </Card>
    </AdminPageLayout>
  );
}
